'use client';

import { Button, Skeleton } from '@common/components';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';
import { QuerySuspenseErrorBoundary } from '@/components/organism/QuerySuspenseErrorBoundary';
import { ROUTES } from '@/constants';
import { useIsGuest } from '@/hooks/useIsGuest';

import { BenefitHomeSectionHeader } from './BenefitHomeSectionHeader';
import { BENEFIT_SECTION_CLASS_NAME } from './BenefitMenuSection';

const FALLBACK_TILE_COUNT = 4;

const CAROUSEL_CLASS_NAME =
  '-mx-20 flex items-start gap-12 overflow-x-auto px-20 py-4 hide-scrollbar webkit-scroll-touch';

const CAROUSEL_ITEM_CLASS_NAME = 'flex shrink-0 even:pt-12';

const CARD_CLASS_NAME = 'h-124 w-103 rounded-medium';

const SUBTITLE_CLASS_NAME = 'font-500 text-Gray-550 typo-body4';

const SUBTITLE_TEXT = '쿠팡으로 모은 제휴포인트로 브랜드 쿠폰에 도전해요';

const PartnerPointBalance = () => {
  const { data: balance } = useSuspenseQuery({
    ...AuthQueryOptions[
      'get:/wello/v1/auth/affiliate/partner-points/summary'
    ](),
    select: ({ context }) => Math.max(0, context?.balance ?? 0),
  });

  return <>{balance.toLocaleString()}</>;
};

const RouletteBrandCarousel = ({ isGuest }: { isGuest: boolean }) => {
  const { data: brands } = useSuspenseQuery({
    ...(isGuest
      ? UnauthQueryOptions[
          'get:/wello/v1/allows/affiliate/partner-points/roulettes'
        ]()
      : AuthQueryOptions[
          'get:/wello/v1/auth/affiliate/partner-points/roulettes'
        ]()),
    select: (response) => response.context?.contents ?? [],
  });

  if (brands.length === 0) {
    return (
      <p className="py-20 text-center font-500 text-Gray-500 typo-body4">
        지금은 참여할 수 있는 쿠폰이 없어요.
      </p>
    );
  }

  return (
    <ul className={CAROUSEL_CLASS_NAME}>
      {brands.map((brand) => (
        <li key={brand.rouletteBrandId} className={CAROUSEL_ITEM_CLASS_NAME}>
          <CustomLink
            //* 룰렛 화면은 진입마다 상세를 새로 조회한다 — 미리 불러오지 않는다.
            prefetch={false}
            href={ROUTES.BENEFIT_PARTNER_POINT_ROULETTE.pathname({
              rouletteBrandId: brand.rouletteBrandId,
            })}
            className={cn(
              CARD_CLASS_NAME,
              'flex flex-col items-center justify-center gap-10 px-8 transition-opacity duration-200 ease-out hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-B-500 active:opacity-80',
              !brand.backgroundColor && 'bg-Gray-50',
            )}
            style={{ backgroundColor: brand.backgroundColor }}
          >
            <span className="relative size-50 shrink-0 overflow-hidden rounded-large bg-Gray-white">
              {brand.imageUrl ? (
                <Image
                  src={brand.imageUrl}
                  alt=""
                  fill
                  sizes="50px"
                  className="object-contain"
                />
              ) : null}
            </span>
            <span
              className={cn(
                'w-full truncate text-center font-700 typo-caption1',
                brand.backgroundColor ? 'text-Gray-white' : 'text-Gray-900',
              )}
            >
              {brand.name}
            </span>
          </CustomLink>
        </li>
      ))}
    </ul>
  );
};

const RouletteBrandCarouselFallback = () => (
  <ul aria-hidden className={CAROUSEL_CLASS_NAME}>
    {Array.from({ length: FALLBACK_TILE_COUNT }, (_, index) => (
      <li key={index} className={CAROUSEL_ITEM_CLASS_NAME}>
        <Skeleton className={CARD_CLASS_NAME} />
      </li>
    ))}
  </ul>
);

export const BenefitPartnerPointShopSection = ({
  isGuest: serverIsGuest,
}: {
  isGuest: boolean;
}) => {
  const isGuest = useIsGuest(serverIsGuest);

  return (
    <section className={BENEFIT_SECTION_CLASS_NAME}>
      <div className="flex flex-col gap-4">
        <BenefitHomeSectionHeader
          icon={<PointCoin currency="AFFILIATE_PARTNER_POINT" size={24} />}
          title="제휴포인트샵"
          moreHref={ROUTES.BENEFIT_PARTNER_POINT_SHOP.pathname}
        />
        {isGuest ? (
          <p className={SUBTITLE_CLASS_NAME}>{SUBTITLE_TEXT}</p>
        ) : (
          <ErrorBoundary
            fallback={<p className={SUBTITLE_CLASS_NAME}>{SUBTITLE_TEXT}</p>}
          >
            <p className={SUBTITLE_CLASS_NAME}>
              쿠팡으로 모은{' '}
              <span className="font-700 text-B-500">
                <Suspense fallback="-">
                  <PartnerPointBalance />
                </Suspense>
                P
              </span>
              로 브랜드 쿠폰에 도전해요
            </p>
          </ErrorBoundary>
        )}
      </div>
      <QuerySuspenseErrorBoundary
        suspenseFallback={<RouletteBrandCarouselFallback />}
        errorFallback={({ resetErrorBoundary }) => (
          <div className="flex min-h-124 flex-col items-center justify-center gap-12">
            <p className="font-500 text-Gray-600 typo-body4">
              불러오지 못했어요
            </p>
            <Button
              theme="sub-line"
              dimensions="H40"
              onClick={resetErrorBoundary}
            >
              다시 시도
            </Button>
          </div>
        )}
      >
        <RouletteBrandCarousel isGuest={isGuest} />
      </QuerySuspenseErrorBoundary>
    </section>
  );
};
